import React, { useEffect, useMemo, useState } from 'react'
import { useFrostTheme } from '../theme/frost'
import CloseIcon from '@mui/icons-material/Close'
import LightModeIcon from '@mui/icons-material/LightMode'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import s from './PriceCalculatorView.module.scss'

// What a job costs us and what to charge for it, in the look of the
// Conceito Verde price list. Each line is some work (its hours) and the things
// it needs (direct costs: props, prints, licences…). What it costs us: hours ×
// the hourly cost, plus the direct costs. The price: the time marked up, the
// direct costs marked up, rounded up. The lines ticked make a quote — a
// discount for taking several together, and IVA on top if wanted.
// Everything recalculates as you type and is kept in this browser.

const KEY = 'priceCalculator.v1'
const IVA = 23
const DEFAULT_SETTINGS = { hourly: 25, markupTime: 100, markupDirect: 15, roundTo: 5, packageDiscount: 10, withIva: false }
const EXAMPLE_LINES = [
  { id: 1, name: 'Product photos', kind: 'half day', hours: 4, direct: 30, on: true },
  { id: 2, name: 'Social video', kind: '30 seconds', hours: 6, direct: 20, on: true },
  { id: 3, name: 'Website', kind: 'five pages', hours: 30, direct: 80, on: false },
  { id: 4, name: 'Ads', kind: 'set up and a month of running', hours: 8, direct: 0, on: false },
]

const num = (v) => { const n = parseFloat(String(v).replace(',', '.')); return isFinite(n) && n > 0 ? n : 0 }
const money = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 })
const euro = (n) => money.format(Math.round(n))
const hrs = (h) => `${Number.isInteger(h) ? h : Math.round(h * 10) / 10} h`

function read() {
  try {
    const saved = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (saved && Array.isArray(saved.lines)) return { settings: { ...DEFAULT_SETTINGS, ...saved.settings }, lines: saved.lines }
  } catch { /* private window, or nothing kept yet */ }
  return { settings: DEFAULT_SETTINGS, lines: EXAMPLE_LINES }
}

/** One line: what it costs us, what to charge, and what's left. */
export function priceLine(line, st) {
  const hours = num(line.hours)
  const direct = num(line.direct)
  const time = hours * num(st.hourly)
  const cost = time + direct
  const raw = time * (1 + num(st.markupTime) / 100) + direct * (1 + num(st.markupDirect) / 100)
  const step = num(st.roundTo)
  const price = step ? Math.ceil(raw / step - 1e-9) * step : raw
  const margin = price - cost
  return { hours, direct, cost, price, margin, marginPct: price ? Math.round((margin / price) * 100) : 0 }
}

export default function PriceCalculatorView({ onClose }) {
  const [theme, toggleTheme] = useFrostTheme()
  const [{ settings, lines }, setState] = useState(read)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ settings, lines })) } catch { /* kept for this visit only */ }
  }, [settings, lines])

  const set = (k, v) => setState(st => ({ ...st, settings: { ...st.settings, [k]: v } }))
  const edit = (id, patch) => setState(st => ({ ...st, lines: st.lines.map(l => (l.id === id ? { ...l, ...patch } : l)) }))
  const add = () => setState(st => ({ ...st, lines: [...st.lines, { id: Date.now(), name: '', kind: '', hours: '', direct: '', on: true }] }))
  const remove = (id) => setState(st => ({ ...st, lines: st.lines.filter(l => l.id !== id) }))
  const reset = () => { if (window.confirm('Put back the example lines and the standard settings?')) setState({ settings: DEFAULT_SETTINGS, lines: EXAMPLE_LINES }) }

  // A client price, with IVA when it's shown.
  const shown = (p) => (settings.withIva ? p * (1 + IVA / 100) : p)
  const rate = num(settings.hourly) * (1 + num(settings.markupTime) / 100)
  const keep = rate - num(settings.hourly)

  const priced = useMemo(() => lines.map(l => ({ line: l, ...priceLine(l, settings) })), [lines, settings])
  const quote = useMemo(() => {
    const chosen = priced.filter(p => p.line.on && (p.hours || p.direct))
    const listPrice = chosen.reduce((a, p) => a + p.price, 0)
    const cost = chosen.reduce((a, p) => a + p.cost, 0)
    const step = num(settings.roundTo)
    // Several things together: the package discount, rounded up as prices are.
    const together = chosen.length >= 2 && num(settings.packageDiscount) > 0
    const raw = together ? listPrice * (1 - num(settings.packageDiscount) / 100) : listPrice
    const net = together && step ? Math.ceil(raw / step - 1e-9) * step : raw
    const margin = net - cost
    return { chosen, listPrice, cost, net, together, margin, marginPct: net ? Math.round((margin / net) * 100) : 0,
      hours: chosen.reduce((a, p) => a + p.hours, 0), direct: chosen.reduce((a, p) => a + p.direct, 0) }
  }, [priced, settings])

  // For the client: what each thing costs them, nothing of ours.
  const copyQuote = async () => {
    const out = [
      ...quote.chosen.map(p => `${[p.line.name, p.line.kind].filter(Boolean).join(' · ') || 'Item'} — ${euro(shown(p.price))}`),
      ...(quote.together ? [`Together, with a ${num(settings.packageDiscount)}% package discount — ${euro(shown(quote.net))}`] : []),
      `Total — ${euro(shown(quote.net))}${settings.withIva ? ` incl. IVA (${IVA}%)` : ' + IVA'}`,
    ].join('\n')
    try { await navigator.clipboard.writeText(out); setCopied(true); setTimeout(() => setCopied(false), 2500) } catch { window.prompt('Copy the quote:', out) }
  }

  const numField = (label, k, { prefix, suffix, step = 1, hint }) => (
    <div className={s.field}>
      <label htmlFor={`pc-${k}`}>{label}</label>
      <div className={s.input}>
        {prefix && <span>{prefix}</span>}
        <input id={`pc-${k}`} type="number" min="0" step={step} inputMode="decimal" value={settings[k]} onChange={e => set(k, e.target.value)} />
        {suffix && <span>{suffix}</span>}
      </div>
      {hint && <p className={s.hint}>{hint}</p>}
    </div>
  )

  return (
    <div className={s.view}>
      <div className={s.corner}>
        <button className={s.round} onClick={toggleTheme} aria-label="Toggle light or dark mode">{theme === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}</button>
        <button className={s.round} onClick={onClose} aria-label="Close"><CloseIcon /></button>
      </div>
      <div className={s.wrap}>
        <header>
          <p className={s.eyebrow}>Work · tools</p>
          <h1>Price calculator</h1>
          <p className={s.lede}>What a job costs us and what to charge for it. Each line shows the hours it takes, its direct costs, what it costs us and the client price. Tick the lines that make a quote; change the settings and every price updates.</p>
        </header>

        <section className={s.settings} aria-labelledby="pc-settings">
          <h2 id="pc-settings">Settings</h2>
          <div className={s.fields}>
            {numField('Your cost per hour', 'hourly', { prefix: '€', hint: 'Your pay, social security, software and equipment, per working hour.' })}
            {numField('Markup on time', 'markupTime', { suffix: '%', step: 5, hint: '100% means you charge double what an hour costs you.' })}
            {numField('Markup on direct costs', 'markupDirect', { suffix: '%', step: 5, hint: 'Props, prints, licences, travel.' })}
            <div className={s.field}>
              <label htmlFor="pc-round">Round prices up to</label>
              <select id="pc-round" value={String(settings.roundTo)} onChange={e => set('roundTo', Number(e.target.value))}>
                {[1, 5, 10, 50].map(v => <option key={v} value={v}>Nearest €{v}</option>)}
              </select>
              <p className={s.hint}>Clients trust round numbers.</p>
            </div>
            {numField('Package discount', 'packageDiscount', { suffix: '%', hint: 'Taken off a quote of two or more lines.' })}
            <div className={s.field}>
              <span aria-hidden="true">&nbsp;</span>
              <label className={s.check}><input type="checkbox" checked={!!settings.withIva} onChange={e => set('withIva', e.target.checked)} /> Show client prices with IVA ({IVA}%)</label>
              <p className={s.hint}>Your cost and margin always exclude IVA.</p>
            </div>
          </div>
          <div className={s.settingsFoot}>
            <p className={s.rate}>Client rate <strong>{euro(rate)}/h</strong> &nbsp;·&nbsp; you keep {euro(keep)} of every hour billed ({rate ? Math.round((keep / rate) * 100) : 0}% margin on time)</p>
            <button className={s.button} type="button" onClick={reset}>Reset to defaults</button>
          </div>
        </section>

        <section className={s.group}>
          <div className={s.groupHead}>
            <h2>Jobs and things</h2>
            <p>Hours of work and direct costs for each. Ticked lines make the quote below.</p>
          </div>
          <div className={s.tableWrap}>
            <table>
              <thead>
                <tr>
                  <th aria-label="In the quote" /><th>Service</th><th className={s.num}>Hours</th><th className={s.num}>Direct costs</th>
                  <th className={s.num}>Your cost</th><th className={s.num}>Client price{settings.withIva ? ' incl. IVA' : ''}</th><th className={s.num}>Your margin</th><th aria-label="Remove" />
                </tr>
              </thead>
              <tbody>
                {priced.map(({ line, cost, price, margin, marginPct }) => (
                  <tr key={line.id} className={line.on ? '' : s.off}>
                    <td className={s.tick}><input type="checkbox" checked={!!line.on} onChange={e => edit(line.id, { on: e.target.checked })} aria-label="In the quote" /></td>
                    <td className={s.svc}>
                      <input className={s.name} value={line.name} placeholder="Service" onChange={e => edit(line.id, { name: e.target.value })} />
                      <input className={s.unit} value={line.kind} placeholder="What it covers — half day, 5 pages…" onChange={e => edit(line.id, { kind: e.target.value })} />
                    </td>
                    <td className={s.num} data-label="Hours"><input type="number" min="0" step="0.5" value={line.hours} onChange={e => edit(line.id, { hours: e.target.value })} aria-label="Hours" /></td>
                    <td className={s.num} data-label="Direct costs"><span className={s.val}><span className={s.cur}>€</span><input type="number" min="0" step="1" value={line.direct} onChange={e => edit(line.id, { direct: e.target.value })} aria-label="Direct costs" /></span></td>
                    <td className={s.num} data-label="Your cost">{euro(cost)}</td>
                    <td className={`${s.num} ${s.price}`} data-label="Client price">{euro(shown(price))}</td>
                    <td className={`${s.num} ${margin < 0 ? s.loss : ''}`} data-label="Your margin"><span className={s.val}>{euro(margin)}<span className={s.pct}>{marginPct}%</span></span></td>
                    <td className={s.del}><button className={s.remove} onClick={() => remove(line.id)} aria-label="Remove"><DeleteOutlineIcon fontSize="small" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button className={s.button} type="button" onClick={add}>+ Add a line</button>
        </section>

        <section className={s.group}>
          <div className={s.groupHead}>
            <h2>The quote</h2>
            <p>{quote.chosen.length ? `${quote.chosen.length} ${quote.chosen.length === 1 ? 'line' : 'lines'} · ${hrs(quote.hours)} of work${quote.together ? ` · ${num(settings.packageDiscount)}% off for taking them together` : ''}` : 'Tick the lines that make it.'}</p>
          </div>
          <div className={s.tableWrap}>
            <table>
              <thead><tr><th>Quote</th><th className={s.num}>Hours</th><th className={s.num}>Direct costs</th><th className={s.num}>Your cost</th><th className={s.num}>Client price{settings.withIva ? ' incl. IVA' : ''}</th><th className={s.num}>Your margin</th></tr></thead>
              <tbody>
                <tr>
                  <td className={s.svc}>
                    <div className={s.nameText}>Total</div>
                    <ul className={s.lines}>
                      {quote.chosen.map(p => <li key={p.line.id}><span>{[p.line.name, p.line.kind].filter(Boolean).join(' · ') || 'Item'}</span><span>{euro(shown(p.price))}</span></li>)}
                    </ul>
                  </td>
                  <td className={s.num} data-label="Hours">{hrs(quote.hours)}</td>
                  <td className={s.num} data-label="Direct costs">{quote.direct ? euro(quote.direct) : '–'}</td>
                  <td className={s.num} data-label="Your cost">{euro(quote.cost)}</td>
                  <td className={`${s.num} ${s.price}`} data-label="Client price">
                    <span className={s.val}>{quote.together && <span className={s.was}>{euro(shown(quote.listPrice))}</span>}{euro(shown(quote.net))}</span>
                  </td>
                  <td className={`${s.num} ${quote.margin < 0 ? s.loss : ''}`} data-label="Your margin"><span className={s.val}>{euro(quote.margin)}<span className={s.pct}>{quote.marginPct}%</span></span></td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className={s.quoteFoot}>
            <button className={s.primary} type="button" onClick={copyQuote} disabled={!quote.chosen.length}>{copied ? 'Copied ✓' : 'Copy the quote for the client'}</button>
            <p className={s.hint}>The copy has the client's prices only — none of your costs or margins.</p>
          </div>
        </section>
      </div>
    </div>
  )
}
